/**
 * Composition root: register built-in entity search providers and default transport.
 *
 * ## Adding a searchable entity
 * 1. Create `modules/<domain>/search/<domain>SearchProvider.ts` implementing
 *    `GlobalSearchSectionProvider` (import types from `@/shared/search/types`).
 *    Reference: `modules/patient/search/patientSearchProvider.ts`.
 * 2. Import and `registerSearchProvider(...)` inside `ensureBuiltinSearchRegistered` below.
 * 3. Add i18n under `common` (e.g. `globalSearchXxx`) and set provider `titleKey`.
 * 4. If the icon is new: extend `GlobalSearchSectionIconId` and map it in `sectionIcons.tsx`.
 * 5. Optional: add cross-entity shortcuts in `shared/layout/spotlight/contextualActions.ts`.
 *
 * Spotlight UI must use `globalSearchClient` only — do not import providers from layout.
 */

import { appointmentSearchProvider } from '@/modules/appointment/search/appointmentSearchProvider';
import { leadSearchProvider } from '@/modules/lead/search/leadSearchProvider';
import { patientSearchProvider } from '@/modules/patient/search/patientSearchProvider';
import { taskSearchProvider } from '@/modules/task/search/taskSearchProvider';
import { GlobalSearchClient } from './client';
import { createFanOutProviderTransport } from './fanOutProviderTransport';
import { registerSearchProvider } from './registry';

let registered = false;

/**
 * Idempotent registration of built-in providers. Call once before Spotlight opens
 * (e.g. from `GlobalSearch` module scope).
 */
export const ensureBuiltinSearchRegistered = (): void => {
  if (registered) return;
  registerSearchProvider(patientSearchProvider);
  registerSearchProvider(leadSearchProvider);
  registerSearchProvider(appointmentSearchProvider);
  registerSearchProvider(taskSearchProvider);
  registered = true;
};

/** Reset registration flag — for tests only. */
export const resetBuiltinSearchRegistration = (): void => {
  registered = false;
};

/** Singleton client for Spotlight; default transport is fan-out over the registry. */
export const globalSearchClient = new GlobalSearchClient(
  createFanOutProviderTransport(),
);
